import { showToast } from 'BreetHelpers';

export interface IEngagementActions {
	isResolve?: boolean;
	onResolve?: () => void;
	shareContentDetails?: IShareContent;
	noReport?: boolean;
	isCopy?: boolean;
	copy?: {
		item: string;
		message: string;
		copyFn?: () => void;
	};
}

const downloadBlob = (blob: Blob, filename: string) => {
	const url = URL.createObjectURL(blob);

	const a = document.createElement('a');
	a.href = url;
	a.download = filename;

	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);

	URL.revokeObjectURL(url);
};

export interface IShareContent {
	title: string;
	text?: string;
	url?: string;
	screenshotElementId?: string;
	action?: 'share' | 'download';
	downloadFn?: () => void;
}

const captureScreenshot = async (elementId: string): Promise<File | undefined> => {
	const html2canvas = (await import('html2canvas')).default;
	const originalElement = document.getElementById(elementId);
	if (!originalElement) throw new Error('Element not found');

	document.body.classList.add('screenshot-mode');

	const PADDING_PIXELS = 30;
	let tempWrapper: HTMLDivElement | null = null;

	tempWrapper = document.createElement('div');

	Object.assign(tempWrapper.style, {
		padding: `${PADDING_PIXELS}px`,
		background: 'white',
		display: 'inline-block',
		boxSizing: 'content-box',
		position: 'relative',
		width: '457px',
		overflow: 'visible',
	});

	tempWrapper.className = 'bg-primary-500';

	originalElement.parentNode?.insertBefore(tempWrapper, originalElement);
	tempWrapper.appendChild(originalElement);

	let screenshotFile: File | undefined;

	try {
		const initialScrollY = window.scrollY;

		await new Promise((res) => requestAnimationFrame(res));
		await new Promise((res) => setTimeout(res, 100));

		const fullHeight = tempWrapper.scrollHeight;
		const fullWidth = tempWrapper.scrollWidth;

		const canvas = await html2canvas(tempWrapper, {
			scrollY: -initialScrollY,
			height: fullHeight,
			width: fullWidth,
			useCORS: true,
			allowTaint: false,
		});

		const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/png'));

		if (!blob) throw new Error('Failed to capture screenshot');

		screenshotFile = new File([blob], 'screenshot.png', { type: 'image/png' });
	} finally {
		if (tempWrapper.parentNode) {
			tempWrapper.parentNode.insertBefore(originalElement, tempWrapper);
			tempWrapper.parentNode.removeChild(tempWrapper);
		}
		document.body.classList.remove('screenshot-mode');
	}

	return screenshotFile;
};

export const shareContent = (shareContentArg?: IShareContent) => async () => {
	if (!shareContentArg) return;

	try {
		const { title, text, url, screenshotElementId, action } = shareContentArg;

		const screenshotFile = screenshotElementId ? await prepareScreenshot(screenshotElementId) : undefined;

		if (action === 'share' && 'share' in navigator) {
			await handleShareAction({ title, text, url, screenshotFile });
			return;
		}

		if (screenshotFile) {
			downloadBlob(screenshotFile, 'shared-content.png');
		}
	} catch (error) {
		handleShareError(error);
	}
};

const prepareScreenshot = async (elementId: string): Promise<File | undefined> => {
	showToast('success', 'Preparing screenshot...');
	const screenshotFile = await captureScreenshot(elementId);

	if (!screenshotFile) {
		showToast('error', 'File not captured. Please try again.');
		return;
	}

	return screenshotFile;
};

const handleShareAction = async ({
	title,
	text,
	url,
	screenshotFile,
}: {
	title?: string;
	text?: string;
	url?: string;
	screenshotFile?: File;
}) => {
	if (screenshotFile && navigator.canShare({ files: [screenshotFile] })) {
		try {
			await navigator.share({ title, text, files: [screenshotFile] });
			return;
		} catch (error) {
			if ((error as Error).name === 'NotAllowedError') {
				showToast('success', 'Downloading instead...');
				downloadBlob(screenshotFile, 'shared-content.png');
				return;
			}
			throw error;
		}
	}

	await navigator.share({ title, text, url });
};

const handleShareError = (error: unknown) => {
	const message = (error as Error).message || String(error);
	if (message.includes('cancel') || message.includes('abort')) return;
	showToast('error', `Sharing failed: ${message}`);
};
